/**
 * Categories Page (story 22770)
 *
 * Browse, create, edit and delete the Survey category tree via
 * /api/Survey/Categories. Writes are Admin-role gated server-side. Delete needs an
 * explicit confirm step and the API's own error text is always shown (a category with
 * children or linked surveys is refused by the server).
 */

import { useCallback, useEffect, useRef, useState, startTransition, type FormEvent } from 'react';
import {
  RefreshCw,
  AlertCircle,
  FolderTree,
  Plus,
  Edit2,
  Trash2,
  X,
  CheckCircle,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import {
  getCategoryTree,
  getCategory,
  createCategory,
  updateCategory,
  deleteCategory,
  type Category,
  type CategoryInput,
  type CategoryTreeNode,
} from '../services/categoriesService';

interface FlatNode {
  node: CategoryTreeNode;
  depth: number;
}

function flatten(node: CategoryTreeNode, depth = 0): FlatNode[] {
  return [{ node, depth }, ...node.inverseParent.flatMap((c) => flatten(c, depth + 1))];
}

function mapTree(
  node: CategoryTreeNode,
  fn: (n: CategoryTreeNode) => CategoryTreeNode,
): CategoryTreeNode {
  const mapped = fn(node);
  return { ...mapped, inverseParent: mapped.inverseParent.map((c) => mapTree(c, fn)) };
}

function removeNode(node: CategoryTreeNode, id: number): CategoryTreeNode {
  return {
    ...node,
    inverseParent: node.inverseParent
      .filter((c) => c.categoryId !== id)
      .map((c) => removeNode(c, id)),
  };
}

const inputClass =
  'w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-sm text-zinc-200 focus:outline-none focus:ring-2 focus:ring-violet-500/50 focus:border-violet-500';

// ---------------------------------------------------------------------------
// Category form (create / edit)
// ---------------------------------------------------------------------------

interface CategoryFormProps {
  idPrefix: string;
  initial?: Partial<CategoryInput>;
  parentOptions?: FlatNode[];
  initialParentId?: number | null;
  onSave: (input: CategoryInput, parentId: number | null) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}

function CategoryForm({
  idPrefix,
  initial,
  parentOptions,
  initialParentId = null,
  onSave,
  onCancel,
  saving,
}: CategoryFormProps) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [shortDescription, setShortDescription] = useState(initial?.shortDescription ?? '');
  const [hidden, setHidden] = useState(initial?.hidden ?? false);
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState(
    initial?.sortOrder === null || initial?.sortOrder === undefined ? '' : String(initial.sortOrder),
  );
  const [parentId, setParentId] = useState<number | null>(initialParentId);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await onSave(
      {
        title: title.trim(),
        shortDescription: shortDescription.trim(),
        hidden,
        isActive,
        sortOrder: sortOrder.trim() === '' ? null : Number(sortOrder),
      },
      parentId,
    );
  };

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className="space-y-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700"
      aria-label={parentOptions ? 'New category' : 'Edit category'}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor={`${idPrefix}-title`} className="block text-xs font-medium text-zinc-400 mb-1">
            Title *
          </label>
          <input
            id={`${idPrefix}-title`}
            type="text"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputClass}
          />
        </div>
        {parentOptions && (
          <div>
            <label htmlFor={`${idPrefix}-parent`} className="block text-xs font-medium text-zinc-400 mb-1">
              Parent category
            </label>
            <select
              id={`${idPrefix}-parent`}
              value={parentId ?? ''}
              onChange={(e) => setParentId(e.target.value === '' ? null : Number(e.target.value))}
              className={inputClass}
            >
              <option value="">None (top level)</option>
              {parentOptions.map(({ node, depth }) => (
                <option key={node.categoryId} value={node.categoryId}>
                  {`${'— '.repeat(depth)}${node.title ?? `Category ${node.categoryId}`}`}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-short`} className="block text-xs font-medium text-zinc-400 mb-1">
          Short description
        </label>
        <input
          id={`${idPrefix}-short`}
          type="text"
          value={shortDescription}
          onChange={(e) => setShortDescription(e.target.value)}
          className={inputClass}
        />
      </div>

      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2">
          <input
            id={`${idPrefix}-active`}
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="w-4 h-4 accent-violet-500"
          />
          <label htmlFor={`${idPrefix}-active`} className="text-xs text-zinc-400">
            Active
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            id={`${idPrefix}-hidden`}
            type="checkbox"
            checked={hidden}
            onChange={(e) => setHidden(e.target.checked)}
            className="w-4 h-4 accent-violet-500"
          />
          <label htmlFor={`${idPrefix}-hidden`} className="text-xs text-zinc-400">
            Hidden
          </label>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor={`${idPrefix}-sort`} className="text-xs text-zinc-400">
            Sort order
          </label>
          <input
            id={`${idPrefix}-sort`}
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className={`${inputClass} w-24`}
          />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving || title.trim() === ''}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-zinc-950 text-sm font-medium hover:bg-violet-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
        >
          {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
          {saving ? 'Saving…' : 'Save Category'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg bg-zinc-700 text-zinc-300 text-sm font-medium hover:bg-zinc-600 transition-colors min-h-[48px]"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CategoriesPage() {
  const [root, setRoot] = useState<CategoryTreeNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [creating, setCreating] = useState<{ parentId: number | null } | null>(null);
  const [editTarget, setEditTarget] = useState<Category | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const savedMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (savedMsgTimerRef.current) clearTimeout(savedMsgTimerRef.current);
  }, []);

  const flashSaved = (msg: string) => {
    setSavedMsg(msg);
    if (savedMsgTimerRef.current) clearTimeout(savedMsgTimerRef.current);
    savedMsgTimerRef.current = setTimeout(() => setSavedMsg(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tree = await getCategoryTree();
      setRoot(tree);
      setExpanded((prev) => (prev.size === 0 ? new Set([tree.categoryId]) : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    startTransition(() => {
      void load();
    });
  }, [load]);

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleCreate = async (input: CategoryInput, parentId: number | null) => {
    setSaving(true);
    setError(null);
    try {
      const parent = parentId === null ? null : await getCategory(parentId);
      const created = await createCategory(input, parent);
      const node: CategoryTreeNode = {
        categoryId: created.categoryId,
        title: created.title ?? input.title,
        shortDescription: created.shortDescription ?? input.shortDescription,
        hidden: created.hidden ?? input.hidden,
        sortOrder: created.sortOrder ?? input.sortOrder,
        inverseParent: [],
      };
      setRoot((prev) =>
        prev === null
          ? prev
          : parentId === null
            ? { ...prev, inverseParent: [...prev.inverseParent, node] }
            : mapTree(prev, (n) =>
                n.categoryId === parentId ? { ...n, inverseParent: [...n.inverseParent, node] } : n,
              ),
      );
      if (parentId !== null) setExpanded((prev) => new Set(prev).add(parentId));
      setCreating(null);
      flashSaved('Category created');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = async (id: number) => {
    setEditLoadingId(id);
    setError(null);
    try {
      setEditTarget(await getCategory(id));
      setCreating(null);
      setConfirmDeleteId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load category');
    } finally {
      setEditLoadingId(null);
    }
  };

  const handleUpdate = async (input: CategoryInput) => {
    if (!editTarget) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateCategory(editTarget, input);
      setRoot((prev) =>
        prev === null
          ? prev
          : mapTree(prev, (n) =>
              n.categoryId === editTarget.categoryId
                ? {
                    ...n,
                    title: updated.title ?? input.title,
                    shortDescription: updated.shortDescription ?? input.shortDescription,
                    hidden: updated.hidden ?? input.hidden,
                    sortOrder: updated.sortOrder ?? input.sortOrder,
                  }
                : n,
            ),
      );
      setEditTarget(null);
      flashSaved('Category updated');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeleting(true);
    setError(null);
    try {
      await deleteCategory(id);
      setRoot((prev) => (prev === null ? prev : removeNode(prev, id)));
      setConfirmDeleteId(null);
      flashSaved('Category deleted');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete category');
      setConfirmDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  const renderNode = (node: CategoryTreeNode, depth: number) => {
    const hasChildren = node.inverseParent.length > 0;
    const isOpen = expanded.has(node.categoryId);
    const name = node.title ?? `Category ${node.categoryId}`;

    if (editTarget?.categoryId === node.categoryId) {
      return (
        <li key={node.categoryId} style={{ marginLeft: depth * 20 }}>
          <CategoryForm
            idPrefix={`cat-edit-${node.categoryId}`}
            initial={{
              title: editTarget.title ?? '',
              shortDescription: editTarget.shortDescription ?? '',
              hidden: editTarget.hidden ?? false,
              isActive: editTarget.isActive,
              sortOrder: editTarget.sortOrder ?? null,
            }}
            onSave={handleUpdate}
            onCancel={() => setEditTarget(null)}
            saving={saving}
          />
        </li>
      );
    }

    return (
      <li key={node.categoryId}>
        <div
          className={`flex items-center gap-2 p-2 rounded-lg border border-zinc-800 bg-zinc-800/30 ${
            node.hidden ? 'opacity-60' : ''
          }`}
          style={{ marginLeft: depth * 20 }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => toggle(node.categoryId)}
              aria-expanded={isOpen}
              aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${name}`}
              className="p-1.5 text-zinc-400 hover:text-zinc-200 rounded-lg hover:bg-zinc-700"
            >
              {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <span className="w-7" aria-hidden="true" />
          )}
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-zinc-200">{name}</span>
            {node.hidden && (
              <span className="ml-2 px-1.5 py-0.5 text-xs rounded border border-zinc-700 text-zinc-500">
                Hidden
              </span>
            )}
            {node.shortDescription && (
              <p className="text-xs text-zinc-500 truncate">{node.shortDescription}</p>
            )}
          </div>

          {confirmDeleteId === node.categoryId ? (
            <div
              role="alertdialog"
              aria-label={`Confirm delete ${name}`}
              className="flex items-center gap-2"
            >
              <span className="text-xs text-red-400">Delete “{name}”? This cannot be undone.</span>
              <button
                type="button"
                onClick={() => void handleDelete(node.categoryId)}
                disabled={deleting}
                className="px-3 py-2 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-500 disabled:opacity-50 min-h-[48px]"
              >
                {deleting ? 'Deleting…' : `Delete ${name}`}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDeleteId(null)}
                className="px-3 py-2 rounded-lg bg-zinc-700 text-zinc-300 text-xs font-medium hover:bg-zinc-600 min-h-[48px]"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  setEditTarget(null);
                  setCreating({ parentId: node.categoryId });
                }}
                className="p-2 text-zinc-400 hover:text-zinc-200 rounded-lg hover:bg-zinc-700"
                aria-label={`Add subcategory to ${name}`}
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => void openEdit(node.categoryId)}
                disabled={editLoadingId === node.categoryId}
                className="p-2 text-zinc-400 hover:text-zinc-200 rounded-lg hover:bg-zinc-700 disabled:opacity-50"
                aria-label={`Edit ${name}`}
              >
                {editLoadingId === node.categoryId ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Edit2 className="w-4 h-4" />
                )}
              </button>
              {depth > 0 && (
                <button
                  type="button"
                  onClick={() => setConfirmDeleteId(node.categoryId)}
                  className="p-2 text-zinc-400 hover:text-red-400 rounded-lg hover:bg-zinc-700"
                  aria-label={`Delete ${name}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          )}
        </div>
        {hasChildren && isOpen && (
          <ul className="space-y-1 mt-1">{node.inverseParent.map((c) => renderNode(c, depth + 1))}</ul>
        )}
      </li>
    );
  };

  const total = root ? flatten(root).length : 0;
  const isEmpty = !loading && !error && root !== null && root.inverseParent.length === 0;

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FolderTree className="w-5 h-5 text-violet-400" />
          <h1 className="text-lg font-semibold text-zinc-100">Categories</h1>
          {!loading && root && <span className="text-xs text-zinc-500">({total})</span>}
        </div>

        <div className="flex items-center gap-2">
          {savedMsg && (
            <span role="status" className="flex items-center gap-1.5 text-xs text-emerald-400">
              <CheckCircle className="w-3.5 h-3.5" />
              {savedMsg}
            </span>
          )}
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="p-1.5 text-zinc-400 hover:text-zinc-200 transition-colors rounded-lg hover:bg-zinc-800"
            aria-label="Refresh categories"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => {
              setEditTarget(null);
              setCreating({ parentId: null });
            }}
            disabled={!root || creating !== null}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-zinc-950 hover:bg-violet-500 transition-colors disabled:opacity-50 min-h-[48px]"
          >
            <Plus className="w-3.5 h-3.5" />
            New Category
          </button>
        </div>
      </div>

      <p className="text-xs text-zinc-500 mb-4">
        The public category tree is cached by the server for up to 24 hours, so the Browse pages may
        take a while to show changes made here.
      </p>

      {error && (
        <div role="alert" className="flex items-center gap-3 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-400 flex-1">{error}</p>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-zinc-500 hover:text-zinc-300"
            aria-label="Dismiss error"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {creating && root && (
        <div className="mb-4">
          <CategoryForm
            key={creating.parentId ?? 'top'}
            idPrefix="cat-new"
            parentOptions={flatten(root)}
            initialParentId={creating.parentId}
            onSave={handleCreate}
            onCancel={() => setCreating(null)}
            saving={saving}
          />
        </div>
      )}

      {loading && (
        <div role="status" aria-label="Loading categories" className="flex items-center justify-center h-32">
          <RefreshCw className="w-5 h-5 text-zinc-500 animate-spin" />
        </div>
      )}

      {isEmpty && (
        <div className="text-center py-10 border border-zinc-800 rounded-lg">
          <FolderTree className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">No categories yet.</p>
        </div>
      )}

      {!loading && root && root.inverseParent.length > 0 && (
        <ul aria-label="Category tree" className="space-y-1">
          {renderNode(root, 0)}
        </ul>
      )}
    </main>
  );
}

export default CategoriesPage;
